import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { EvaluationTagDto } from '../../services/service-evaluations';
import HistoricalFields from '../app-evaluation-start/HistoricalFields';
import TagPicker from '../app-evaluation-start/TagPicker';
import ProjectField from './ProjectField';
import { ContextChoice, WizardAction, WizardState } from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

type Props = {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  tagIds: string[];
  tags: EvaluationTagDto[];
  tagsLoaded: boolean;
  onTagsChange: (tags: EvaluationTagDto[]) => void;
};

const CHOICES: { key: ContextChoice; label: [string, string]; hint: [string, string] }[] = [
  {
    key: 'project',
    label: ['wizard.context.existing', 'Befintligt projekt'],
    hint: ['wizard.context.existingHint', 'Koppla till ett projekt i Accurator'],
  },
  {
    key: 'historical',
    label: ['wizard.context.historical', 'Historiskt projekt'],
    hint: ['wizard.context.historicalHint', 'Namn och nummer, inget inköp behövs'],
  },
  {
    key: 'none',
    label: ['wizard.context.none', 'Inget projekt'],
    hint: ['wizard.context.noneHint', 'Utvärdera leverantören generellt'],
  },
];

const ContextStep = ({ state, dispatch, tagIds, tags, tagsLoaded, onTagsChange }: Props) => {
  const [t] = useTranslation('evaluations');

  return (
    <div className={styles.fields}>
      <div className={styles.fieldStack}>
        <p className={styles.sectionLabel}>{t('wizard.context.label', 'Projekt')}</p>
        <div className={styles.choices}>
          {CHOICES.map(choice => {
            const selected = state.contextChoice === choice.key;
            return (
              <button
                key={choice.key}
                type="button"
                className={classNames(styles.choice, { [styles.selected]: selected })}
                aria-pressed={selected}
                data-context-card={choice.key}
                onClick={() => dispatch({ type: 'chooseContext', choice: choice.key })}
              >
                <span className={styles.choiceName}>{t(...choice.label)}</span>
                <span className={`${styles.small} ${styles.muted}`}>{t(...choice.hint)}</span>
              </button>
            );
          })}
        </div>
      </div>
      {state.contextChoice === 'project' &&
        (state.projectToLoad ? (
          <p className={styles.hint}>{t('wizard.context.projectLoading', 'Hämtar projektet…')}</p>
        ) : (
          <div className={styles.sideBySide}>
            <ProjectField
              value={state.project}
              onChange={next => dispatch({ type: 'setProject', project: next })}
            />
          </div>
        ))}
      {state.contextChoice === 'historical' && (
        <div className={styles.fieldStack}>
          <div className={styles.sideBySide}>
            <HistoricalFields
              value={{
                historicalName: state.historicalName,
                historicalNumber: state.historicalNumber,
              }}
              onChange={next => dispatch({ type: 'setHistorical', ...next })}
            />
          </div>
          <p className={styles.hint}>
            {t('wizard.context.numberHelp', 'Samma nummer grupperar svaren som ett projekt.')}
          </p>
        </div>
      )}
      <div className={styles.fieldStack}>
        <TagPicker
          value={tagIds}
          onChange={ids => dispatch({ type: 'setTags', tagIds: ids })}
          prefilled={!state.tagsTouched}
          tags={tags}
          onTagsChange={onTagsChange}
          tagsLoaded={tagsLoaded}
          label={t('wizard.context.tagsLabel', 'Taggar (valfritt)')}
        />
        <p className={styles.hint}>
          {t('wizard.context.tagsHint', 'Taggarna syns bara internt, aldrig för mottagarna.')}
        </p>
      </div>
    </div>
  );
};

export default ContextStep;
